import { formatRegionLocation } from '../utils/regionLocation';
import React from 'react';
import { Alert, Box, TextField, Typography } from '@mui/material';
import { InstanceTypeInfo } from '../types/providers';
import { AwsTypePicker, useAwsCatalog } from './AwsComputeSettings';

export const awsParentRegion = (code: string) =>
  code.match(/^([a-z]{2}(?:-[a-z]+)+-\d+)-[a-z0-9]+-\d+[a-z]$/)?.[1] || code;

export interface LocationLaunchConfig {
  key_pair_name?: string | null;
  subnet_id?: string | null;
  security_group_id?: string | null;
  instance_type?: string | null;
}

export default function AwsLocationSettings({ code, value, onChange, dockerRuntime = false, instanceTypes = [], defaultInstanceType, providerId }: {
  providerId?: string;
  code: string;
  value?: LocationLaunchConfig | null;
  onChange: (value: LocationLaunchConfig) => void;
  dockerRuntime?: boolean;
  instanceTypes?: InstanceTypeInfo[];
  defaultInstanceType?: string;
}) {
  const catalog = useAwsCatalog(dockerRuntime ? undefined : providerId, code);
  const parent = awsParentRegion(code);
  const local = parent !== code;
  const selectedType = value?.instance_type || defaultInstanceType;
  const capacity = instanceTypes.find(entry => entry.type === selectedType)?.bots_per_host;
  return <Box sx={{ display: 'grid', gap: 2 }}>
    <Typography variant="subtitle2">AWS launch settings{local ? ' · Local Zone' : ''}</Typography>
    {local && <Alert severity="info">
      Parent region: {formatRegionLocation(parent)}. Use an AMI from this region and a subnet in {code} with an internet gateway route.
      The zone must be opted in. Instance types vary by zone; launch validates availability and architecture.
    </Alert>}
    {!dockerRuntime && <TextField label="Subnet ID" value={value?.subnet_id || ''}
      helperText={local ? 'Select the Local Zone subnet. If blank, discovery requires exactly one public subnet in this zone.' : 'Optional override for this location.'}
      onChange={e => onChange({ ...value, subnet_id: e.target.value.trim() })} fullWidth />}
    {!dockerRuntime && <TextField label="Security group ID" value={value?.security_group_id || ''}
      helperText="Must belong to the subnet’s VPC. Blank uses the provider’s security group."
      onChange={e => onChange({ ...value, security_group_id: e.target.value.trim() })} fullWidth />}
    {dockerRuntime ? <Alert severity="info">
      Docker shared host: {selectedType || 'not selected'} · {capacity ?? 'unconfigured'} bots per host.
      Select the host type and capacity for this location under Provider Configuration.
      Host network and operator key are managed by the fleet launch template.
    </Alert> : <>
      {catalog.error && <Alert severity="error">{catalog.error}</Alert>}
      <AwsTypePicker items={catalog.items} value={selectedType || ''} loading={catalog.loading}
        onChange={item => onChange({ ...value, instance_type: item.type })} />
    </>}
    {!dockerRuntime && <TextField label="Operator key pair" value={value?.key_pair_name || ''}
      helperText="Optional parent-region EC2 key pair override."
      onChange={e => onChange({ ...value, key_pair_name: e.target.value.trim() })} fullWidth />}
  </Box>;
}
